import { AuthPage, ThemedTitleV2 } from "@refinedev/antd";
import type { ForgotPasswordFormTypes } from "@refinedev/core";
import { useForgotPassword, useLink, useLogin, useRouterContext, useRouterType, useTranslate } from "@refinedev/core";
import type { StepsProps } from "antd";
import { Button, Card, Col, ConfigProvider, Form, Layout, Row, Steps, theme, Typography } from "antd";
import type { FormProviderProps } from "antd/es/form/context";
// import { SendCode } from "../components";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ModalHandle } from "ui";
import { BrandFooter, Modal } from "ui";
import { AppIcon } from "ui/app-icon";
import { FormInput, FormInputPassword, FormItem } from "ui/form";
import { useMessage } from "utils";

import Logo from "@/assets/logo.png";

import { ForgotPasswordResult, ResetPassword, VerifyAccount } from "./components";
import { bodyStyles, containerStyles, headStyles, layoutStyles, titleStyles } from "./index.style";

const { Text, Title } = Typography;
const { useToken } = theme;

export const _ForgotPassword = () => {
  const { token } = useToken();
  const [form] = Form.useForm<ForgotPasswordFormTypes>();
  const translate = useTranslate();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();

  const ActiveLink = routerType === "legacy" ? LegacyLink : Link;
  const title = <ThemedTitleV2 collapsed={false} icon={<AppIcon />} text={null} />;

  const { mutate: forgotPassword, isLoading } = useForgotPassword<ForgotPasswordFormTypes>();

  const PageTitle = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: "32px",
        fontSize: "20px",
      }}
    >
      {title}
    </div>
  );

  const CardTitle = (
    <Title
      level={3}
      style={{
        color: token.colorPrimaryTextHover,
        ...titleStyles,
      }}
    >
      {translate("pages.forgotPassword.title", "Forgot your password?")}
    </Title>
  );
  const CardContent = (
    <Card
      bodyStyle={bodyStyles}
      headStyle={headStyles}
      style={{
        ...containerStyles,
        backgroundColor: token.colorBgElevated,
      }}
      title={CardTitle}
    >
      <Form<ForgotPasswordFormTypes>
        form={form}
        layout="vertical"
        onFinish={(values) => forgotPassword(values)}
        requiredMark={false}
      >
        <FormInput
          label={translate("pages.forgotPassword.fields.email", "Email")}
          maxLength={128}
          name="email"
          placeholder={translate("pages.forgotPassword.fields.email", "Email")}
          rules={[
            { required: true },
            {
              type: "email",
              message: translate("pages.forgotPassword.errors.validEmail", "Invalid email address"),
            },
          ]}
          size="large"
          type="email"
        />
        <FormItem label={translate("pages.forgotPassword.fields.code", "Code")} name="code">
          <SendCode name="email" />
        </FormItem>
        <FormInputPassword
          label={translate("pages.forgotPassword.fields.password", "Password")}
          name="password"
          placeholder={translate("pages.forgotPassword.fields.password", "Password")}
          rules={[{ required: true }]}
          size="large"
        />
        <FormInputPassword
          label={translate("pages.forgotPassword.fields.confirmPassword", "New Password")}
          name="confirmPassword"
          placeholder={translate("pages.forgotPassword.fields.confirmPassword", "New Password")}
          rules={[{ required: true }]}
          size="large"
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Text
            style={{
              fontSize: 12,
              marginLeft: "auto",
            }}
          >
            {translate("pages.register.buttons.haveAccount", "Have an account? ")}{" "}
            <ActiveLink
              style={{
                fontWeight: "bold",
                color: token.colorPrimaryTextHover,
              }}
              to="/login"
            >
              {translate("pages.login.signin", "Sign in")}
            </ActiveLink>
          </Text>
        </div>
        <Form.Item
          style={{
            marginTop: "24px",
            marginBottom: 0,
          }}
        >
          <Button block htmlType="submit" loading={isLoading} size="large" type="primary">
            {translate("pages.forgotPassword.buttons.submit", "Send reset instructions")}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  return (
    <Layout style={layoutStyles}>
      <Row
        align="middle"
        justify="center"
        style={{
          height: "100vh",
        }}
      >
        <Col xs={22}>
          {PageTitle}
          {CardContent}
        </Col>
      </Row>
    </Layout>
  );
};

interface ForgotPasswordForm {
  email: string;
  code: string;
  password: string;
}
export const ForgotPassword = () => {
  const modalRef = useRef<ModalHandle>(null);
  const [isLoading, setIsLoading] = useState();
  const { mutateAsync: mutateAsyncForgotPassword, isLoading: isForgotPasswordLoading } = useForgotPassword();
  const { mutateAsync: loginAsync, isLoading: isLoginLoading } = useLogin();
  const navigate = useNavigate();
  const { contextHolder, message } = useMessage();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ForgotPasswordForm>({
    email: "",
    code: "",
    password: "",
  });

  useEffect(() => {
    modalRef.current?.open();
  }, []);

  const nextStep = useCallback(() => setCurrentStep((step) => ++step), []);

  const stepsItem = [
    { title: "アカウント確認" },
    { title: "パスワード設定" },
    // { title: "完了" },
  ] satisfies StepsProps["items"];

  const onFormFinish: FormProviderProps["onFormFinish"] = async (name, info) => {
    if (name === "verify") {
      setFormData((prev) => ({ ...prev, ...info.values }));
      nextStep();
    } else if (name === "reset") {
      const { password } = info.values;
      const { email, code } = formData;
      setFormData((prev) => ({ ...prev, password }));
      try {
        const { success, redirectTo, error } = await mutateAsyncForgotPassword({
          email,
          code,
          password,
        });
        if (success) {
          message.success("パスワードの変更が成功しました。");
          await loginAsync({ email, password });
          navigate(redirectTo || "/");
        } else {
          throw error;
        }
      } catch (error) {
        navigate("/login", { replace: true });
      }
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: { bodyBg: "url('/auth_bg.png') no-repeat fixed" },
        },
      }}
    >
      {contextHolder}
      <AuthPage
        loginLink={false}
        renderContent={() => (
          <>
            <Modal maskClosable={false} onCancel={() => navigate(-1)} ref={modalRef} title="パスワードをお忘れの方">
              <Form.Provider onFormFinish={onFormFinish}>
                <Steps current={currentStep} items={stepsItem} />
                <div className="mt-6 px-6">
                  {currentStep === 0 && <VerifyAccount />}
                  {currentStep === 1 && <ResetPassword isNextButtonLoading={isLoading} />}
                  {/* {currentStep === 2 && <ForgotPasswordResult />} */}
                </div>
              </Form.Provider>
            </Modal>
            <BrandFooter />
          </>
        )}
        title={<ThemedTitleV2 collapsed={false} icon={<img height="32" src={Logo} width="32" />} text={null} />}
        type="forgotPassword"
      />
    </ConfigProvider>
  );
};
